import { loadIdentity, joinToken } from './identity';
import { Net } from './net';
import { NET_ON } from './server-messages';
import { registerModules } from '../modules';
import { AV } from '../media/meeting/av';
import { Sfu } from '../media/meeting/sfu';
import { syncMedia } from '../media/meeting/sync';
import { initToolbar, layout, note, refresh } from '../screen/meeting/toolbar';
import { initChat, chatCount, chatVisible } from '../screen/meeting/chat';
import { initViewer } from '../screen/meeting/viewer';
import { initUsage } from '../screen/meeting/usage';
import { boardFeed, initBoard, openShare, refreshShare } from '../screen/meeting/board';
import { SCHOOL_GROUP } from '../../../shared/world/links';
import { askName, initEntry, initRoom, roomName } from '../screen/core/entry';
import { posOf, peersIn, updatePeers, type Body } from '../../2-controllers/core/peers';
import { seatTaken } from '../../2-controllers/meeting/peers';
import { linkGroupOf, updateLinks } from '../../2-controllers/meeting/links';
import { currentShare, handUp } from '../../2-controllers/meeting/share';
import { isTeacherPeer, myKinds, teacherHere } from '../../2-controllers/meeting/classroom';
import { CLASS, setNotice, takeCls } from '../../3-store/meeting/classroom';
import { isMeeting } from '../../../shared/world/links';
import { ME, setMyHand, setMyPlace, setMySeat, setMyShare, setMyX } from '../../3-store/core/me';
import { GROUP } from '../../3-store/meeting/links';
import { CHAT, LOG_SEC, tickChat } from '../../3-store/meeting/chat';
import { LINK, peers, setLink } from '../../3-store/core/peers';
import { LOOK_COUNT } from '../../../shared/limits';
import { stageOf, stagePlace, type PlaceId, type StageId } from '../../../shared/ids';
import { stagePose } from '../../2-controllers/game/stage-peers';
import { SEATS } from '../../../shared/world/seats';

// 옛 스크립트(js/town.js, scenes.js, main.js)와 서버를 잇는 다리: window.G.presence, G.askName, G.onScene.
// 옛 스크립트는 import를 못 하므로 G 하나로 부른다. 연결·위치 보내기·받기·앉은 자리 알리기가 여기서 일어난다.
// 5단계에서 town이 TypeScript가 되면 이 다리 없이 컨트롤러를 직접 부른다.
const G = (window as any).G;
let name = '', look = -1, started = false, curRoom: PlaceId | null = null;
const sent = { seat: -1, share: 0, hand: 0 };   // 서버가 아는 내 값 (새 접속은 서 있음·공유 없음·손 내림)
const resetSent = () => { sent.seat = -1; sent.share = 0; sent.hand = 0; };

// 지금 고른 친구(G.save.data.hero)의 번호 = 외형 번호(0 ~ LOOK_COUNT-1)
const lookIndex = () => Math.max(0, Math.min(LOOK_COUNT - 1, G.CRITTERS.findIndex((k: any) => k.id === G.save.data.hero)));

// 연결한다. 이름이나 친구가 바뀌었으면 다시 접속한다(같은 토큰이라 같은 사람이다)
function ensure() {
  const n = G.save.data.name || '손님', l = lookIndex();
  if (started && n === name && l === look) return;
  started = true; name = n; look = l;
  setLink('connecting');
  Net.start(roomName(), () => ({ t: 'join', name, look, token: joinToken() }), {
    ...NET_ON,
    welcome(m: any) { resetSent(); NET_ON.welcome!(m); },   // 새로 접속하면 서버는 나를 서 있음으로 안다
    // 내 공유가 거절됐거나(다른 사람이 공유 중) 서버가 내렸다: 공유를 멈춘다
    meet(m: any) {
      NET_ON.meet!(m);
      if (m.id === LINK.id && m.k === 'share' && m.v === 0) { sent.share = 0; AV.setShare(false); }
      if (m.id === LINK.id && m.k === 'hand' && m.v === 0) { sent.hand = 0; setMyHand(false); }   // 발언권을 받으면 서버가 손을 내린다
    },
  });   // 새로 접속하면 서버는 나를 서 있는 것으로 안다
}

// 내 회의 상태(앉은 자리·화면 공유·손들기)를 바뀐 것만 서버에 알린다: 자리가 먼저(모임이 바뀔 수 있으니), 위치(pos)보다 먼저. 연결이 없으면 다음 프레임에 다시
function syncMeet() {
  const now = { seat: G.seated ? (G.seated.seat as number) : -1, share: AV.state.share ? 1 : 0, hand: ME.hand ? 1 : 0 };
  for (const k of ['seat', 'share', 'hand'] as const) if (now[k] !== sent[k] && Net.send({ t: 'meet', k, v: now[k] })) sent[k] = now[k];
}
// 교실 수업 (docs 9장): 강사가 있는 학교에서 말할 자격이 없는 트랙은 끈다(자격은 강사가 앉거나 발언권이 거둬질 때 바뀐다). 발언권 요청은 서버로 보내고, 쌓인 안내는 도구 막대에 한 번 보인다
const KIND_OFF: Record<string, [string, () => Promise<boolean>]> = { mic: ['마이크', () => AV.setMic(false)], cam: ['카메라', () => AV.setCam(false)], share: ['화면 공유', () => AV.setShare(false)] };
function followClass() {
  const may = myKinds(), off = (['mic', 'cam', 'share'] as const).filter((k) => AV.state[k] && !may.includes(k));
  off.forEach((k) => KIND_OFF[k][1]());
  if (off.length) setNotice(`강사가 수업 중이라 ${off.map((k) => KIND_OFF[k][0]).join('·')}을 껐어요. 손을 들어 발언권을 받으세요`);
  for (const c of takeCls()) Net.send({ t: 'cls', k: c.k, id: c.id });
  if (CLASS.notice) { note(CLASS.notice, 6000); setNotice(''); }
}
// 모임이 바뀌었으면(자리·장소) 서버가 공유와 손을 내렸다: 화면도 같이 내린다
let lastGroup: string | null = null;
function followGroup() {
  if (GROUP.key === lastGroup) return;
  lastGroup = GROUP.key;
  sent.share = 0; sent.hand = 0;
  setMyHand(false); if (AV.state.share) AV.setShare(false);
}

// 영상 칸 이름표: 사람마다 이름과 친구 색. 바뀐 때만 영상 쪽에 넣는다
let namesKey = '';
function syncNames() {
  const col = (look: number) => (G.CRITTERS[look] || G.CRITTERS[0]).col, m: Record<string, { name: string; color: string; hand: boolean }> = {};
  peers.forEach((r) => { m[r.id] = { name: r.name, color: col(r.look), hand: handUp(r) }; });
  if (LINK.id) m[LINK.id] = { name: G.save.data.name || '손님', color: col(lookIndex()), hand: ME.hand };
  const k = JSON.stringify(m);
  if (k !== namesKey) { namesKey = k; AV.setNames(m); }
}
// 영상 모임: 모임이 바뀌었을 수 있는 순간(장소·자리)에는 바로, 아니면 0.25초마다 연결 대상을 다시 정하고, 영상·송출·도구 막대를 맞춘다
let countN = -1;
function frameMeeting(force = false) {
  updateLinks(1 / 60, force);
  followGroup(); setMyShare(!!AV.state.share); followClass();
  syncMedia(); syncNames(); syncShare(); layout(); refresh(); if (Net.online && peers.size + 1 !== countN) chatCount(countN = peers.size + 1);
}
// 화면 공유 영상: 내 모임에서 공유하는 사람의 영상을 칠판·큰 칸·겹침창에 넘긴다
let localShare: { track: MediaStreamTrack; stream: MediaStream } | null = null;
function syncShare() {
  const sh = currentShare();
  let stream: MediaStream | null = null;
  if (sh && sh.mine) {
    const t = AV.localTrack('share');
    if (t) { if (!localShare || localShare.track !== t) localShare = { track: t, stream: new MediaStream([t]) }; stream = localShare.stream; }
  } else if (sh && sh.id) stream = AV.remoteShare(sh.id);
  refreshShare(sh && stream ? { stream, name: sh.mine ? G.save.data.name || '손님' : sh.name, school: GROUP.key === SCHOOL_GROUP } : null);
}

const presence = {
  // 마을·건물 안의 장소가 열렸다
  enter(room: PlaceId) { ensure(); curRoom = room; setMyPlace(room); frameMeeting(true); },
  // 모험 스테이지가 열렸다: 같은 스테이지에 있는 사람끼리 서로 보이고 음성이 이어진다(적·아이템·죽음은 각자). 앉은 자리는 스테이지에 없다
  enterStage(id: StageId) { G.seated = null; this.enter(stagePlace(id)); },
  // 장소가 도는 동안 프레임마다: 앉은 자리 알리기, 위치 보내기, 다른 사람 위치 갱신. h = 내 몸
  update(dt: number, h: Body) {
    if (!curRoom) return;
    syncMeet();
    const seat = G.seated ? G.seated.seat : -1, changed = seat !== ME.seat;
    setMySeat(seat); setMyX(h.x, h.y);
    Net.sendPos(posOf(curRoom, h));
    updatePeers(dt); tickChat(dt);
    updateLinks(dt, changed);
    followGroup(); setMyShare(!!AV.state.share); followClass();
    syncMedia(); syncNames(); syncShare(); layout(); refresh(); if (Net.online && peers.size + 1 !== countN) chatCount(countN = peers.size + 1);
  },
  // 장소를 떠났다(지도·제목 화면). 다른 사람 화면에서 내가 사라진다
  away() {
    if (!curRoom) return;
    curRoom = null; resetSent(); Net.sendAway();
    setMyPlace(null); AV.setMic(false); AV.setCam(false);   // 장소를 떠나면 마이크·카메라도 끈다
    frameMeeting(true);
  },
  // 이 장소에서 그릴 다른 사람들
  peersIn(room: PlaceId) {
    return peersIn(room).map((r) => ({
      id: r.id, name: r.name, kind: (G.CRITTERS[r.look] || G.CRITTERS[0]).id, x: r.x, y: r.y, vx: r.vx, vy: r.vy, face: r.face, st: r.st, t: r.t, runT: r.runT, blinkT: r.blinkT,
      say: (CHAT.bubbles.get(r.id) || {}).text || null, mic: !!(AV.peerState(r.id) || {}).mic, sharing: !!r.sh && isMeeting(linkGroupOf(ME)) && linkGroupOf(r) === linkGroupOf(ME),
      hand: handUp(r), teacher: teacherHere() && isTeacherPeer(r), granted: !!r.gr && teacherHere(), seat: SEATS[r.seat] && SEATS[r.seat].room === r.room ? r.seat : -1, back: !!(SEATS[r.seat] && SEATS[r.seat].room === r.room && SEATS[r.seat].back),
    }));
  },
  // 지금 내가 달리는 스테이지에서 함께 달리는 사람들: 그릴 위치(스테이지 픽셀)와 모습(G.drawCritter 옵션), 이름표 값
  stagePeers() {
    if (!curRoom || !stageOf(curRoom)) return [];
    return peersIn(curRoom).map((r) => {
      const look = G.CRITTERS[r.look] || G.CRITTERS[0];
      return { id: r.id, name: r.name, col: look.col, x: r.x, y: r.y, mic: !!(AV.peerState(r.id) || {}).mic, pose: stagePose({ kind: look.id, face: r.face, st: r.st, vx: r.vx, vy: r.vy, t: r.t, runT: r.runT, blinkT: r.blinkT }) };
    });
  },
  // 스테이지 지도에서: 스테이지마다 지금 그 안에 있는 다른 사람들 { 스테이지 아이디: [{ name, col }] }
  stageFriends() {
    const out: Record<string, { name: string; col: string }[]> = {};
    for (const r of peers.values()) { const st = stageOf(r.room); if (st) (out[st] ||= []).push({ name: r.name, col: (G.CRITTERS[r.look] || G.CRITTERS[0]).col }); }
    return out;
  },
  seatTaken: (seat: number) => seatTaken(peers.values(), seat),
  get myHand() { return ME.hand; },
  get myGranted() { return CLASS.granted && teacherHere(); },   // 강사가 있는 교실에서 내가 발언권을 가졌다
  get mySay() { return (CHAT.bubbles.get('me') || {}).text || null; },
  get myMic() { return AV.state.mic; },
  // 채팅창이 닫혀 있을 때 화면 아래에 잠깐 그리는 최근 대화 { name, text, mine, alpha }
  recentChat() { return chatVisible() ? [] : CHAT.log.map((l) => ({ ...l, alpha: Math.max(0, Math.min(1, (LOG_SEC - (CHAT.now - l.at)) / 2)) })).filter((l) => l.alpha > 0); },
  // 학교 칠판에 그릴 공유 영상 { video, name, ready } 또는 null, 겹침창 열기
  board: boardFeed,
  openShare,
  get online() { return Net.online; },
};

initToolbar(); initBoard(); initChat(); initViewer(); initUsage();
registerModules();   // meeting을 core의 표에 이어 붙인다 (첫 프레임보다 먼저)
initRoom(); initEntry(); loadIdentity();
G.presence = presence;
G.askName = askName;
G.onScene = (s: any) => { if (s.stage) presence.enterStage(s.stage); else if (!s.place) presence.away(); };   // 스테이지는 장소다(같은 스테이지 사람끼리 보인다). 장소도 스테이지도 아닌 장면(제목·지도)에 들어가면 사라진다
(window as any).leaf = { presence, peers, LINK, Net, AV, Sfu, ME };   // 시험용 손잡이 (test/two-tabs.js)
